import { catchError, useMemoAsync, useUrlParam } from "@opendash/core";
import { useTranslation } from "@opendash/i18n";
import { Source } from "@opendash/plugin-parse/dist/types-generated";
import {
  Button,
  Card,
  DatePicker,
  Divider,
  Flex,
  Form,
  Input,
  Select,
  StepProps,
  Steps,
} from "antd";
import dayjs from "dayjs";
import { observer } from "mobx-react-lite";
import Parse from "parse";
import React, { useEffect, useMemo, useState } from "react";
import { SelectMedia } from "../../../_shared/media/components/SelectMedia";
import {
  Item,
  Schedule_Execution,
  Schedule_Execution_Step,
} from "../../../js/parsegenerated";
import { Material } from "../../../types/Material";
import { getMetaValuesString } from "../../sourcemeta/components/SourceMetaViewer";
import {
  SaveExecutionValues,
  useSaveExecution,
} from "../hooks/useSaveExecution";
import ItemSelectionList from "./ItemSelectionList";
import StepSelection from "./StepSelection";

type Props = {
  sources: Source[];
  editExecution?: Schedule_Execution;
  reload?: any;
  onSaveSuccess?: () => void;
  dateField?: boolean;
};

type ItemFieldType = {
  [index: string]: Array<{ selected: Material }>;
};

type FormValues = {
  sources: string[];
  steps: string[];
  items: ItemFieldType;
  title: string;
  description: string;
  date?: dayjs.Dayjs;
};

type StepPropsExtended = StepProps & { content: React.JSX.Element };

export default observer((props: Props) => {
  const t = useTranslation();
  const [form] = Form.useForm<FormValues>();
  const [step, setStep] = useState<number>(0);
  const [steps, setSteps] = useState<Schedule_Execution_Step[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [parseFiles, setParseFiles] = useState<Parse.File[]>([]);
  const [searchString] = useUrlParam<string>("search", "", "string");
  const [selectedSources, setSelectedSources] = useState<Source[]>(undefined);
  const { saveExistingExecution, saveNewExecution } = useSaveExecution();

  const sourceQuery = new Parse.Query(Source).containedIn(
    "objectId",
    props.sources.map((s) => s.id)
  );

  const hasItems = useMemoAsync(
    () => new Parse.Query(Item).matchesQuery("sources", sourceQuery).find(),
    [props.sources]
  );

  useEffect(() => {
    reset();
  }, [props.reload]);

  const filteredSources = useMemo(() => {
    if (!searchString) return props.sources;

    return props.sources.filter((source) => {
      let found = true;
      const metaString = getMetaValuesString(source);
      const splittedSearchParams = searchString
        .split(",")
        ?.map((s) => s.trim());

      splittedSearchParams.forEach((searchValue) => {
        if (!metaString.toLowerCase().includes(searchValue.toLowerCase())) {
          found = false;
        }
      });

      return found;
    });
  }, [searchString, props.sources]);

  useEffect(() => {
    if (props.editExecution === undefined) {
      return;
    }

    //Get all steps
    catchError(
      new Parse.Query(Schedule_Execution_Step)
        .equalTo("scheduleexecution", props.editExecution)
        .find()
    ).then((stepsresult) => {
      setSteps(stepsresult[1]);
    });
  }, [props.editExecution]);

  // setting the field values for the edit
  useEffect(() => {
    if (props.editExecution && form) {
      props.editExecution
        .get("media")
        .query()
        .find()
        .then((media) => {
          setParseFiles(media.map((m) => m.get("media")));
        });

      form.setFieldsValue({
        date: dayjs(props.editExecution.get("finishedAt")),
        title: props.editExecution.get("title"),
        description: props.editExecution.get("description"),
        steps: steps.map((step) => step.id),
        sources: [props.editExecution.get("source").id],
        items: props.editExecution
          .get("material")
          ?.reduce((acc: Material, material: Material) => {
            if (acc[props.editExecution.get("source").id] === undefined) {
              acc[props.editExecution.get("source").id] = [];
            }

            if (material !== undefined && material !== null) {
              acc[props.editExecution.get("source").id].push({
                selected: material.id,
                count: material.quantity,
                unit: material.unit,
              });
            }

            return acc;
          }, {} as ItemFieldType),
      });
    }
  }, [props.editExecution, props.reload, form]);

  const stepItems: StepPropsExtended[] = useMemo(() => {
    const steps: StepPropsExtended[] = [
      {
        title: t(
          "maintenance:schedules.multi-source-schedule-modal.date.title"
        ),
        content: (
          <Form.Item
            name="date"
            label={t(
              "maintenance:schedules.multi-source-schedule-modal.date.label"
            )}
          >
            <DatePicker style={{ width: "100%" }} showTime />
          </Form.Item>
        ),
      },
      {
        title: t(
          "maintenance:schedules.multi-source-schedule-modal.sources.title"
        ),
        content: (
          <Form.Item
            name="sources"
            label={t(
              "maintenance:schedules.multi-source-schedule-modal.sources.label"
            )}
            initialValue={filteredSources.map((source) => source.id)}
            rules={[
              {
                validator(rule, value) {
                  const valueSources = filteredSources.filter((source) =>
                    value.includes(source.id)
                  );
                  setSelectedSources(valueSources);
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Select
              mode="multiple"
              showSearch
              optionFilterProp="label"
              filterOption={(input, option) =>
                option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              loading={loading}
              options={filteredSources.map((source) => ({
                value: source.id,
                label: source.name,
              }))}
            />
          </Form.Item>
        ),
      },
      {
        title: t(
          "maintenance:schedules.multi-source-schedule-modal.steps.label"
        ),
        content: <StepSelection execution={props.editExecution} form={form} />,
      },
      {
        title: t(
          "maintenance:schedules.multi-source-schedule-modal.items.title"
        ),
        // should items be selected per source / execution? or only once for all executions?
        content: !!hasItems && (
          <Flex vertical gap="16px">
            {(selectedSources ?? filteredSources).map((sSource) => {
              return <ItemSelectionList key={sSource.id} source={sSource} />;
            })}
          </Flex>
        ),
      },
      {
        title: t(
          "maintenance:schedules.multi-source-schedule-modal.media.title"
        ),
        content: (
          <SelectMedia parseFiles={parseFiles} setParseFiles={setParseFiles} />
        ),
      },
      {
        title: t("maintenance:schedules.multi-source-schedule-modal.details"),
        content: (
          <>
            <Form.Item
              name="title"
              label={t(
                "maintenance:schedules.multi-source-schedule-modal.title"
              )}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="description"
              label={t(
                "maintenance:schedules.multi-source-schedule-modal.description"
              )}
            >
              <Input.TextArea />
            </Form.Item>
          </>
        ),
      },
    ];

    steps.forEach((step, index) => {
      step.onClick = () => setStep(index);
      step.style = { cursor: "pointer" };
    });

    return steps;
  }, [filteredSources, selectedSources]);

  async function onSave(values: FormValues) {
    setLoading(true);

    try {
      const saveValues: SaveExecutionValues = {
        description: values.description,
        files: parseFiles,
        sources: selectedSources,
        steps: values.steps,
        title: values.title,
        items: values.items,
        date: values.date ? new Date(values.date.toISOString()) : new Date(),
      };

      if (props.editExecution) {
        await saveExistingExecution(saveValues, props.editExecution);
      } else {
        await saveNewExecution(saveValues);
      }

      form.resetFields();
      props.onSaveSuccess && props.onSaveSuccess();
    } catch (e) {
      console.error(e);
    }

    reset();
  }

  function reset() {
    setStep(0);
    setLoading(false);
    form.resetFields();
  }

  return (
    <Form form={form} onFinish={onSave} layout="vertical">
      <Card style={{ marginBottom: 32 }}>
        <Steps items={stepItems} current={step} />
      </Card>
      {stepItems.map((stepItem: StepPropsExtended, index) => {
        return (
          <Card
            key={index}
            style={{
              display:
                stepItem.title === stepItems[step].title ? "block" : "none",
            }}
          >
            {stepItem.content}
          </Card>
        );
      })}
      <Divider />
      <Flex justify="space-between" style={{ width: "100%" }}>
        <div>
          {step > 0 && (
            <Button onClick={() => setStep((prev) => prev - 1)}>
              {t("maintenance:global.button.back_label")}
            </Button>
          )}
        </div>
        <div>
          {step < stepItems.length - 1 && (
            <Button
              onClick={() => setStep((prev) => prev + 1)}
              style={{ alignSelf: "flex-end" }}
            >
              {t("maintenance:global.button.continue_label")}
            </Button>
          )}
          {step === stepItems.length - 1 && (
            <Button onClick={form.submit} loading={loading} type="primary">
              {t("maintenance:global.button.save_label")}
            </Button>
          )}
        </div>
      </Flex>
    </Form>
  );
});
